import React from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  TablePagination,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";

export const BULK_ACTIONS = {
  VIEW: "view",
  EDIT: "edit",
  DELETE: "delete",
};

const ACTION_META = {
  [BULK_ACTIONS.VIEW]: {
    label: "View",
    Icon: VisibilityIcon,
    color: "primary.main",
  },
  [BULK_ACTIONS.EDIT]: {
    label: "Edit",
    Icon: EditIcon,
    color: "info.main",
  },
  [BULK_ACTIONS.DELETE]: {
    label: "Delete",
    Icon: DeleteIcon,
    color: "error.main",
  },
};

export const BulkActionOptionLabel = ({ action, label, large = false }) => {
  const meta = ACTION_META[action];
  if (!meta) return label || action;
  const { Icon, color } = meta;
  const isDelete = action === BULK_ACTIONS.DELETE;
  const useLarge = large || isDelete;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: useLarge ? 1.25 : 1 }}>
      <Icon sx={{ fontSize: useLarge ? "1.5rem" : "1.1rem", color }} />
      <Typography
        component="span"
        variant={useLarge ? "body1" : "body2"}
        sx={{ fontWeight: 600, fontSize: useLarge ? "1.02rem" : undefined }}
      >
        {label || meta.label}
      </Typography>
    </Box>
  );
};

/** Task rows with per-row `_accessFlags`. Bulk bar shows delete only (view/edit via row name + modal). */
export const buildTaskBulkActionOptions = (
  selectedRows,
  { canEdit, canMemberDeleteRow }
) => {
  const deletable = selectedRows.filter((row) => canEdit && canMemberDeleteRow(row));
  if (deletable.length > 0) {
    return [{ value: BULK_ACTIONS.DELETE, label: "Delete" }];
  }
  return [];
};

/** Templates: delete only in bulk bar (view/edit via row name + modal). */
export const buildTemplateBulkActionOptions = (selectedRows, { canEdit, canEditRow }) => {
  const editable =
    typeof canEditRow === "function"
      ? selectedRows.filter((row) => canEditRow(row))
      : canEdit
        ? selectedRows
        : [];
  if (editable.length > 0) {
    return [{ value: BULK_ACTIONS.DELETE, label: "Delete" }];
  }
  return [];
};

const footerBarSx = (bordered, borderFn, hasPagination, inset) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: hasPagination ? "space-between" : "flex-start",
  gap: 1.5,
  width: "100%",
  boxSizing: "border-box",
  ...(bordered && borderFn ? { borderTop: borderFn } : {}),
  px: bordered || inset ? 1.5 : 0,
  py: 0.75,
  bgcolor: (theme) =>
    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.02)",
});

export const TaskTableBulkActionsBar = ({
  totalCount,
  selectedCount,
  bulkAction,
  onBulkActionChange,
  bulkActionOptions,
  onApply,
  canApply,
  hasSelection,
  bordered = false,
  bulkBarInset = false,
  borderTop,
  pagination,
}) => {
  const showPagination = pagination != null;

  return (
    <Box sx={footerBarSx(bordered, borderTop, showPagination, bulkBarInset)}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.25,
          flex: "1 1 auto",
          minWidth: 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            letterSpacing: 0.3,
            fontSize: { xs: "0.82rem", sm: "0.88rem" },
            color: "text.primary",
            whiteSpace: "nowrap",
          }}
        >
          SELECTED: {selectedCount} / {totalCount}
        </Typography>
        <Select
          size="small"
          displayEmpty
          value={bulkAction}
          disabled={!hasSelection || bulkActionOptions.length === 0}
          onChange={(e) => onBulkActionChange(e.target.value)}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "0.82rem", fontWeight: 600, letterSpacing: 0.3 }}
                >
                  SELECT ACTION
                </Typography>
              );
            }
            const opt = bulkActionOptions.find((o) => o.value === selected);
            return <BulkActionOptionLabel action={selected} label={opt?.label} />;
          }}
          sx={{
            minWidth: { xs: 168, sm: 200 },
            maxWidth: 260,
            fontSize: "0.95rem",
            bgcolor: "background.paper",
            "& .MuiSelect-select": { py: 1.1, display: "flex", alignItems: "center" },
            "&.Mui-disabled": {
              bgcolor: (theme) =>
                theme.palette.mode === "dark" ? "action.disabledBackground" : "action.hover",
            },
          }}
        >
          <MenuItem value="" disabled>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.3 }}>
              SELECT ACTION
            </Typography>
          </MenuItem>
          {bulkActionOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ py: 1.15 }}>
              <BulkActionOptionLabel action={opt.value} label={opt.label} large />
            </MenuItem>
          ))}
        </Select>
        <Button
          size="medium"
          variant="contained"
          color="primary"
          disabled={!canApply}
          onClick={onApply}
          sx={{
            minWidth: 88,
            minHeight: 40,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.95rem",
            px: 2,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          Apply
        </Button>
      </Box>
      {showPagination && (
        <TablePagination
          component="div"
          {...pagination}
          sx={{
            flex: "0 0 auto",
            width: "auto",
            m: 0,
            ml: "auto",
            ...(pagination.sx || {}),
          }}
        />
      )}
    </Box>
  );
};
